import { base64UrlDecode } from './moderatorAuth.js';

const MAX_TIMEOUT_MS = 2147483647;

let expiryTimer = null;

function getStore() {
  return require('@/store/index').default;
}

function getRouter() {
  return require('@/router').default;
}

function requestPath(config) {
  return String(config?.url || '');
}

function isCredentialRequest(config) {
  const url = requestPath(config);
  return url.includes('/login') || url.includes('/register');
}

export function getJwtExpiry(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

export function isJwtExpired(token) {
  if (!token || typeof token !== 'string') return true;
  const parts = token.split('.');
  if (parts.length !== 3) return true;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

export function clearSessionExpiryTimer() {
  if (expiryTimer != null) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
}

export function routeRequiresAuth(route) {
  return Boolean(route?.matched?.some((record) => record.meta && record.meta.requiresAuth));
}

/**
 * An expired token is cleared. Public routes stay where they are.
 * Only a route that requires auth is sent to login.
 */
export function resolveAuthGate({ hasToken, tokenExpired, requiresAuth, path }) {
  const logout = Boolean(hasToken && tokenExpired);
  const tokenRemains = Boolean(hasToken) && !tokenExpired;
  if (requiresAuth && !tokenRemains && path !== '/login') {
    return { logout, redirect: '/login' };
  }
  return { logout, redirect: null };
}

export function forceLogoutToLogin() {
  clearSessionExpiryTimer();
  const store = getStore();
  if (store.state.token) {
    store.commit('LOGOUT');
  }
  const router = getRouter();
  const current = router.currentRoute?.value;
  if (routeRequiresAuth(current) && current?.path !== '/login') {
    router.push('/login');
  }
}

export function scheduleSessionExpiryLogout(token) {
  clearSessionExpiryTimer();
  const expMs = getJwtExpiry(token);
  if (expMs == null) return;
  const delay = expMs - Date.now();
  if (delay <= 0) {
    forceLogoutToLogin();
    return;
  }
  expiryTimer = setTimeout(() => {
    expiryTimer = null;
    scheduleSessionExpiryLogout(token);
  }, Math.min(delay, MAX_TIMEOUT_MS));
}

/**
 * @param {import('axios').AxiosInstance} client
 * @param {{ optional?: boolean }} [options] optional true (default): send JWT if present.
 *   optional false: reject with 'User not authenticated' when no token (apiAuthClient).
 */
export function attachAuth(client, { optional = true } = {}) {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
      if (!optional) {
        return Promise.reject('User not authenticated');
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 401 && !isCredentialRequest(error?.config)) {
        forceLogoutToLogin();
      }
      return Promise.reject(error);
    }
  );

  return client;
}
